import { api } from './api';

export const commsApi = {
  config: () => api.get('/v2/comms/config').then(r => r.data),
  listConversations: (params = {}) => api.get('/v2/comms/conversations', { params }).then(r => r.data),
  // Ticket 142: one row per contact (paged, filtered) and the contact's
  // merged, cross-channel timeline — the Inbox list/thread use these instead
  // of listConversations/listMessages.
  inbox: (params = {}) => api.get('/v2/comms/inbox', { params }).then(r => r.data),
  contactMessages: (contactId, params = {}) => api.get(`/v2/comms/contacts/${encodeURIComponent(contactId)}/messages`, { params }).then(r => r.data),
  // Ticket 154: cheap poll target for the nav badge and the paused notice.
  inboxSummary: (params = {}) => api.get('/v2/comms/inbox/summary', { params }).then(r => r.data),
  // Ticket 154 P3: per-user opt-in for browser notifications on new client texts.
  setNotifyPrefs: (body) => api.put('/v2/comms/inbox/notify-prefs', body).then(r => r.data),
  // Ticket 195: star a contact's conversation, and mark several read / unread
  // (both written to GoHighLevel first; a bulk answer lists what failed).
  starContact: (contactId, starred) => api.post(`/v2/comms/inbox/contacts/${encodeURIComponent(contactId)}/star`, { starred }).then(r => r.data),
  markInbox: (contactIds, read) => api.post('/v2/comms/inbox/mark', { contact_ids: contactIds, read }).then(r => r.data),
  // Ticket 195: the user's own email signature.
  getSignature: () => api.get('/v2/comms/inbox/signature').then(r => r.data),
  setSignature: (signature) => api.put('/v2/comms/inbox/signature', { signature }).then(r => r.data),
  markContactRead: (contactId) => api.post(`/v2/comms/contacts/${encodeURIComponent(contactId)}/read`).then(r => r.data),
  getConversation: (id) => api.get(`/v2/comms/conversations/${id}`).then(r => r.data),
  createConversation: (contact_id, channel = 'sms') => api.post('/v2/comms/conversations', { contact_id, channel }).then(r => r.data),
  sendStatus: (contactId) => api.get(`/v2/comms/contacts/${encodeURIComponent(contactId)}/send-status`).then(r => r.data),
  listMessages: (id) => api.get(`/v2/comms/conversations/${id}/messages`).then(r => r.data),
  sendMessage: (id, payload) => api.post(`/v2/comms/conversations/${id}/messages`, payload).then(r => r.data),
  markRead: (id) => api.post(`/v2/comms/conversations/${id}/read`).then(r => r.data),
  // Ticket 135 S1: fetched lazily (only when a call/voicemail bubble is
  // played), never eagerly on thread load.
  getRecording: (messageId) => api.get(`/v2/comms/messages/${messageId}/recording`, { responseType: 'blob' }).then(r => r.data),
  // Ticket 195: the composer picker reads; only "Manage staff" writes.
  listTemplates: (params = {}) => api.get('/v2/comms/templates', { params }).then(r => r.data),
  createTemplate: (p) => api.post('/v2/comms/templates', p).then(r => r.data),
  updateTemplate: (id, p) => api.patch(`/v2/comms/templates/${encodeURIComponent(id)}`, p).then(r => r.data),
  deleteTemplate: (id) => api.delete(`/v2/comms/templates/${encodeURIComponent(id)}`).then(r => r.data),
  listSuppression: () => api.get('/v2/comms/suppression').then(r => r.data),
  addSuppression: (p) => api.post('/v2/comms/suppression', p).then(r => r.data),
  removeSuppression: (channel, address, reason) => api.delete('/v2/comms/suppression', { params: { channel, address, reason } }).then(r => r.data),
};

export const partnersApi = {
  list: (opts = {}) => api.get('/v2/partners', { params: opts.includeInactive ? { include_inactive: true } : {} }).then(r => r.data),
  create: (p) => api.post('/v2/partners', p).then(r => r.data),
  update: (id, p) => api.patch(`/v2/partners/${id}`, p).then(r => r.data),
  del: (id) => api.delete(`/v2/partners/${id}`).then(r => r.data),
  regenToken: (id) => api.post(`/v2/partners/${id}/regenerate-token`).then(r => r.data),
  sendLink: (id) => api.post(`/v2/partners/${id}/send-link`).then(r => r.data),
  referrals: (id) => api.get(`/v2/partners/${id}/referrals`).then(r => r.data),
};

export const orgApi = {
  listBranches: () => api.get('/v2/org/branches').then(r => r.data),
  createBranch: (p) => api.post('/v2/org/branches', p).then(r => r.data),
  updateBranch: (id, p) => api.patch(`/v2/org/branches/${id}`, p).then(r => r.data),
  deleteBranch: (id) => api.delete(`/v2/org/branches/${id}`).then(r => r.data),
  listTeams: () => api.get('/v2/org/teams').then(r => r.data),
  createTeam: (p) => api.post('/v2/org/teams', p).then(r => r.data),
  updateTeam: (id, p) => api.patch(`/v2/org/teams/${id}`, p).then(r => r.data),
  addTeamMember: (id, userId) => api.post(`/v2/org/teams/${id}/members`, { user_id: userId }).then(r => r.data),
  removeTeamMember: (id, userId) => api.delete(`/v2/org/teams/${id}/members/${userId}`).then(r => r.data),
  deleteTeam: (id) => api.delete(`/v2/org/teams/${id}`).then(r => r.data),
  listPlans: () => api.get('/v2/org/commission-plans').then(r => r.data),
  createPlan: (p) => api.post('/v2/org/commission-plans', p).then(r => r.data),
  updatePlan: (id, p) => api.patch(`/v2/org/commission-plans/${id}`, p).then(r => r.data),
  deletePlan: (id) => api.delete(`/v2/org/commission-plans/${id}`).then(r => r.data),
  compute: (p) => api.post('/v2/org/commissions/compute', p).then(r => r.data),
  createRecord: (p) => api.post('/v2/org/commissions/records', p).then(r => r.data),
  listRecords: (params = {}) => api.get('/v2/org/commissions/records', { params }).then(r => r.data),
  payrollCsvPath: (period) => `/v2/org/commissions/payroll.csv?period=${encodeURIComponent(period)}`,
  getPermissions: () => api.get('/v2/org/permissions/matrix').then(r => r.data),
  setPermissions: (matrix) => api.post('/v2/org/permissions/matrix', matrix).then(r => r.data),
  listMembers: () => api.get('/v2/org/members').then(r => r.data),
  killSessions: () => api.post('/v2/org/incident/kill-sessions').then(r => r.data),
  killLinks: (includeApiKeys) => api.post('/v2/org/incident/kill-links', { include_api_keys: !!includeApiKeys }).then(r => r.data),
  killMemberSessions: (userId) => api.post(`/v2/org/members/${userId}/kill-sessions`).then(r => r.data),
  transferOwnership: (userId) => api.post('/v2/org/ownership/transfer', { user_id: userId }).then(r => r.data),
};
